import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CheckCircle2, ArrowRight, Users } from "lucide-react";
import { api } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { PageBanner, SectionHeading, PILLARS, Txt } from "@/components/site/Brand";
import { ServiceCard, PublicationCard } from "@/components/site/Cards";
import { CtaBand } from "@/components/site/Layout";
import NotFound from "./NotFound";

export function Services() {
  const { services, settings } = useSite();
  useEffect(() => { document.title = "Services – IRID"; }, []);
  return (
    <>
      <PageBanner title="Our services" eyebrow="Services" intro={settings?.services_intro} crumbs={[{ to: "/", label: "Home" }, { label: "Services" }]} testid="services-banner" />
      <section className="bg-irid-mist py-20">
        <div className="container-irid grid gap-6 md:grid-cols-2" data-testid="services-grid">
          {services.map((s, i) => <ServiceCard key={s.slug} s={s} index={i} />)}
        </div>
      </section>
      <CtaBand />
    </>
  );
}

export function ServiceDetail() {
  const { slug } = useParams();
  const [s, setS] = useState(undefined);
  useEffect(() => {
    let live = true;
    setS(undefined);
    api.get(`/services/${slug}`).then((r) => { if (live) { setS(r.data); document.title = `${r.data.title} – IRID`; } }).catch(() => live && setS(null));
    return () => { live = false; };
  }, [slug]);
  if (s === null) return <NotFound />;
  if (!s) return <div className="min-h-[60vh]" aria-busy="true" />;
  const p = PILLARS[s.pillar] || PILLARS.blue;
  return (
    <>
      <PageBanner title={s.title} eyebrow="Service pillar" intro={s.service_summary} accent={p.bar}
        crumbs={[{ to: "/", label: "Home" }, { to: "/services", label: "Services" }, { label: s.title }]} testid="service-banner">
        <Link to={`/contact?form=consultancy&service=${s.slug}`} className="btn-highlight mt-8" data-testid="service-banner-cta">{s.cta_label}</Link>
      </PageBanner>
      <section className="bg-white py-20">
        <div className="container-irid grid gap-14 lg:grid-cols-12">
          <div className="space-y-12 lg:col-span-8">
            {s.service_details.map((d) => (
              <div key={d.heading} data-testid={`service-detail-${d.heading.toLowerCase().replace(/\s+/g, "-")}`}>
                <h2 className="text-2xl md:text-3xl">{d.heading}</h2>
                <div className={`mt-3 h-1 w-12 rounded ${p.bar}`} aria-hidden="true" />
                {d.body && <Txt as="p" className="mt-5 text-lg">{d.body}</Txt>}
                {d.paragraphs?.map((t, i) => <p key={i} className="mt-5 max-w-[70ch] text-lg">{t}</p>)}
                {d.items && (
                  <ul className="mt-6 grid gap-3 sm:grid-cols-2">
                    {d.items.map((it) => <li key={it} className="flex gap-3 rounded-lg bg-irid-mist p-4"><CheckCircle2 aria-hidden="true" className={`mt-1 h-4 w-4 shrink-0 ${p.icon}`} /><span>{it}</span></li>)}
                  </ul>
                )}
              </div>
            ))}
          </div>
          <aside className="lg:col-span-4">
            <div className={`card-irid card-top ${p.border} sticky top-28 p-7`} data-testid="service-intended-clients">
              <div className="flex items-center gap-3"><Users aria-hidden="true" className={`h-6 w-6 ${p.icon}`} /><h2 className="text-xl">Who this is for</h2></div>
              <ul className="mt-5 space-y-3">
                {s.intended_clients.map((c, i) => <li key={i} className="flex gap-3"><CheckCircle2 aria-hidden="true" className={`mt-1 h-4 w-4 shrink-0 ${p.icon}`} /><Txt>{c}</Txt></li>)}
              </ul>
              <Link to={`/contact?form=consultancy&service=${s.slug}`} className="btn-primary mt-7 w-full" data-testid="service-aside-cta">{s.cta_label}</Link>
            </div>
          </aside>
        </div>
      </section>
      {s.related_publications.length > 0 && (
        <section className="bg-irid-mist py-20" data-testid="service-related-publications">
          <div className="container-irid">
            <SectionHeading eyebrow="Related research" title="Related publications" />
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">{s.related_publications.map((x, i) => <PublicationCard key={x.slug} pub={x} index={i} />)}</div>
          </div>
        </section>
      )}
      <section className="bg-white py-16" data-testid="service-related-services">
        <div className="container-irid">
          <h2 className="text-2xl">Related services</h2>
          <ul className="mt-6 grid gap-4 md:grid-cols-3">
            {s.related_services.map((r) => {
              const rp = PILLARS[r.pillar] || PILLARS.blue;
              return (
                <li key={r.slug}><Link to={`/services/${r.slug}`} className={`card-irid hoverable card-top ${rp.border} flex items-center justify-between gap-3 p-5 font-heading font-semibold text-irid-navy`}>
                  <span className="flex items-center gap-3"><rp.Icon aria-hidden="true" className={`h-5 w-5 ${rp.icon}`} />{r.title}</span><ArrowRight aria-hidden="true" className="h-4 w-4 text-irid-blue" />
                </Link></li>
              );
            })}
          </ul>
        </div>
      </section>
      <CtaBand title={s.cta_heading} text={`Discuss your ${s.title.toLowerCase()} needs with the IRID team.`} cta={s.cta_label} to={`/contact?form=consultancy&service=${s.slug}`} secondary={s.cta_secondary || null} />
    </>
  );
}
